import { isNotNullOrUndefined } from '@vy/utils';
import React from 'react';
import { Helmet } from 'react-helmet';
import { useQueryClient } from 'react-query';
import { withGlobalSearchParams } from '../../../components/utility/GlobalSearchParamsBoundary';
import { withSuspenseBoundary } from '../../../components/utility/SuspenseBoundary';
import { useContractPage } from '../../../hooks/requests/pages/use-contract-page';
import { prefetchContractBillingSchedule } from '../../../hooks/requests/use-contract-billing-schedule';
import { useValidatedParams } from '../../../hooks/use-validated-params';
import { hasNetSuiteIntegration } from '../../../utils/integration-utils';
import { ContractContent } from '../contract-page-components/ContractContent/ContractContent';
import { ContractContentLoader } from '../contract-page-components/ContractContent/ContractContentLoader';
import { ContractPageFallbackComponent } from '../contract-page-components/ContractPageFallbackComponent';

const CustomerContractPage: React.FC = () => {
  const { contractId } = useValidatedParams<['contractId']>('contractId');
  const queryClient = useQueryClient();

  // Overlap with the page-data fetch; the billing-schedule suspense hook below would otherwise waterfall.
  void prefetchContractBillingSchedule(contractId, queryClient);

  const { data } = useContractPage(contractId, {
    suspense: true,
  });

  const contract = data?.contractPageData.contract;
  const isContractHasPhases = (data?.contractPageData?.versions.length ?? 0) > 1;

  const isNetSuiteSynced = hasNetSuiteIntegration(contract?.integrationEntities);

  return (
    <>
      <Helmet>
        <title>Customer Contract</title>
      </Helmet>

      {isNotNullOrUndefined(data?.contractPageData) && (
        <ContractContent
          contractPageData={data.contractPageData}
          customFieldsWithDefinitions={data.customFieldsWithDefinitions}
          customFieldDefinitions={data.customFieldDefinitions}
          isEditable={Boolean(contract?.isEditable)}
          isNetSuiteSynced={isNetSuiteSynced}
          isContractHasPhases={isContractHasPhases}
        />
      )}
    </>
  );
};

const ContractPageWithSuspenseBoundary = withGlobalSearchParams(
  withSuspenseBoundary(CustomerContractPage, {
    errorBoundaryProps: {
      fallback: () => <ContractPageFallbackComponent text="No Contracts Found" />,
    },
    suspenseProps: {
      fallback: <ContractContentLoader />,
    },
  }),
);
export { ContractPageWithSuspenseBoundary as CustomerContractPage };
